"use client";

import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Tooltip,
  Pagination,
} from "@mui/material";
import moment from "moment";
import EmptyState from "./EmptyState";
import AppSelectField from "./AppSelectField";
import PreloaderTable from "./PreloaderTable";

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

const formatDate = (value, format = "DD MMM YYYY") => {
  if (!value || !moment(value).isValid()) return "N/A";
  return moment(value).format(format);
};

const formatCurrency = (value) => (value === null || value === undefined ? "N/A" : value);

const formatStatusValue = (value, config) => {
  if (!value) return "N/A";
  return config?.[value]?.label || value;
};

const renderStatusUI = (value, config) => {
  if (!value) return "N/A";

  const status = config?.[value];

  if (status?.className) {
    return <div className={status.className}>{status.label || value}</div>;
  }

  return <Chip size="small" variant="outlined" label={formatStatusValue(value, config)} />;
};

const resolveCellValue = (row, col, statusConfig) => {
  const value = row[col.key];

  if (col.render) {
    return col.render(value, row);
  }

  switch (col.type) {
    case "status":
      return renderStatusUI(value, statusConfig[col.key]);

    case "date":
      return formatDate(value, col.format);

    case "currency":
      return formatCurrency(value);

    default:
      return value ?? "N/A";
  }
};

const applyFilters = (data, columns, filtersEnabled, filterValues) => {
  if (!filtersEnabled) return data;

  return data.filter((row) =>
    columns.every((col) => {
      const filterValue = filterValues[col.key];
      if (!col.filter || !filterValue) return true;

      const cellValue = row[col.key];

      switch (col.filter) {
        case "text":
          return String(cellValue ?? "")
            .toLowerCase()
            .includes(filterValue.toLowerCase());

        case "select":
          return cellValue === filterValue;

        case "date":
          return moment(cellValue).isValid() && moment(cellValue).format("YYYY-MM-DD") === filterValue;

        default:
          return true;
      }
    }),
  );
};

/* -------------------------------------------------------------------------- */
/* Row Action Renderer                                                        */
/* -------------------------------------------------------------------------- */

const RowActionRenderer = ({ actions = [], row }) => {
  if (!actions.length) return null;

  return (
    <Box display="flex" gap={0.5} justifyContent="center">
      {actions.map((action, index) => {
        if (typeof action.render === "function") {
          return (
            <Box key={index} onClick={(e) => e.stopPropagation()}>
              {action.render(row)}
            </Box>
          );
        }

        const disabled = action.isDisabled?.(row);

        if (action.icon) {
          return (
            <Tooltip key={index} title={action.tooltip || ""} arrow disableHoverListener={!action.tooltip}>
              <span>
                <IconButton
                  size="small"
                  color={action.color || "default"}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick?.(row);
                  }}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (action.label) {
          return (
            <Button
              key={index}
              size="small"
              variant={action.variant || "outlined"}
              color={action.color || "secondary"}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.(row);
              }}
              sx={{
                minWidth: 36,
                height: 36,
                px: 1,
                borderRadius: 1,
                textTransform: "capitalize",
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              {action.label}
            </Button>
          );
        }

        return null;
      })}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const DataTable = ({
  data = [],
  columns = [],
  page = 1,
  pageSize = 10,
  totalPages = 1,
  selectable = false,
  selectableConfig = {},
  filters = false,
  rowActions,
  onRowClick,
  isLoading = false,
  statusConfig = {},
  onSelectionChange,
  renderCell,
  onExport,
  getRowId = (row) => row.id,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 30, 40, 50],
}) => {
  const safePage = Number.isInteger(page) && page > 0 ? page : 1;
  const safePageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;
  const safeTotalPages = Number.isInteger(totalPages) && totalPages > 0 ? totalPages : 1;
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [filterValues, setFilterValues] = useState({});

  /* ------------------------------ Selection -------------------------------- */

  const toggleRow = useCallback(
    (row, e) => {
      e?.stopPropagation();
      const id = getRowId(row);

      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    },
    [getRowId],
  );

  useEffect(() => {
    if (!onSelectionChange) return;

    const selectedRows = data.filter((row) => selectedRowIds.has(getRowId(row)));

    onSelectionChange(selectedRows);
  }, [selectedRowIds, data, getRowId, onSelectionChange]);

  /* ------------------------------ Filtering -------------------------------- */

  const handleFilterChange = useCallback((key, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const filteredData = useMemo(() => applyFilters(data, columns, filters, filterValues), [data, columns, filters, filterValues]);

  /* ------------------------------ Loading ---------------------------------- */

  if (isLoading) {
    return <PreloaderTable isLoading={true} />;
  }

  if (!filteredData.length) {
    return (
      <Paper>
        <Box p={3}>
          <EmptyState isEmpty={true} />
        </Box>
      </Paper>
    );
  }

  const extraColumns = 1 + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  return (
    <>
      <Paper>
        {onExport && (
          <Box p={2} display="flex" justifyContent="flex-end">
            <Button size="small" onClick={() => onExport(filteredData)}>
              Export
            </Button>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>S/N</TableCell>

                {selectable && <TableCell width={48} />}

                {columns.map((col) => (
                  <TableCell key={col.key} width={col.width}>
                    {col.header}
                  </TableCell>
                ))}

                {rowActions && <TableCell align="center">Actions</TableCell>}
              </TableRow>

              {filters && (
                <TableRow>
                  <TableCell />
                  {selectable && <TableCell />}

                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {col.filter === "text" && (
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Filter"
                          value={filterValues[col.key] || ""}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                        />
                      )}

                      {col.filter === "date" && (
                        <TextField
                          size="small"
                          type="date"
                          fullWidth
                          value={filterValues[col.key] || ""}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                        />
                      )}
                    </TableCell>
                  ))}

                  {rowActions && <TableCell />}
                </TableRow>
              )}
            </TableHead>

            <TableBody>
              {filteredData.map((row, index) => {
                const rowId = getRowId(row);

                return (
                  <TableRow key={rowId} hover={!!onRowClick} onClick={() => onRowClick?.(row)}>
                    <TableCell>{(safePage - 1) * safePageSize + index + 1}</TableCell>

                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedRowIds.has(rowId)}
                          disabled={selectableConfig.isEligible && !selectableConfig.isEligible(row)}
                          onChange={(e) => toggleRow(row, e)}
                        />
                      </TableCell>
                    )}

                    {columns.map((col) => (
                      <TableCell key={col.key}>{renderCell ? renderCell(row, col) : resolveCellValue(row, col, statusConfig)}</TableCell>
                    ))}

                    {rowActions && (
                      <TableCell align="center">
                        <RowActionRenderer actions={rowActions.actions} row={row} />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}

              {!filteredData.length && (
                <TableRow>
                  <TableCell colSpan={columns.length + extraColumns} align="center">
                    <EmptyState isEmpty={true} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <div className="my-3">
        <div className="d-flex align-items-center gap-4 justify-content-between">
          <div>
            <AppSelectField
              value={String(safePageSize)}
              placeholder="Limit"
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              items={pageSizeOptions.map((size) => ({
                label: String(size),
                value: String(size),
              }))}
            />
          </div>
          <div>
            <Pagination
              page={safePage}
              count={safeTotalPages}
              onChange={(_, value) => onPageChange?.(value)}
              shape="rounded"
              color="primary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DataTable;
